import TileWrapper from '../TileWrapper'
import { Link } from 'react-router-dom'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { useOverviewRuntime, useTile } from '../../context'
import LinkButton from '@/components/ui/link-button'
import NewsItem from 'app/frontend/base/pages/shared/News/NewsItem'

dayjs.extend(relativeTime)

const RecentNewsTile = () => {
  const { tile } = useTile()
  const { overviewData, ownerName, ownerPath, recentNewsEditPermission } =
    useOverviewRuntime()
  const newsItems = overviewData?.newsItems || []

  const visibleNewsItems = newsItems.slice(0, tile.config?.amount || 2)
  const newsPath = `${ownerPath}/news`
  const compact = tile.config?.compact !== false
  const showViewAll = tile.config?.show_view_all !== false
  const hasMoreNewsItems = newsItems.length > visibleNewsItems.length

  if (tile.position === 'sidebar') {
    return (
      <TileWrapper>
        <ul className='space-y-3'>
          {visibleNewsItems.length === 0 ? (
            <p className='text-muted-foreground'>No news items found.</p>
          ) : null}

          {visibleNewsItems.map((item) => (
            <li
              key={item.id}
              className={'border-t pt-2 first:pt-0 first:border-t-0'}
            >
              <Link
                to={`${ownerPath}/news/${item.id}`}
                className='flex justify-between gap-3 group'
              >
                <div>
                  <p className='font-medium group-hover:text-primary transition-colors'>
                    {item.title}
                  </p>
                  <p className='text-sm text-muted-foreground'>
                    {dayjs(item.firstPublishedAt).fromNow()}
                  </p>
                </div>

                {item.mainImageUrl && (
                  <img
                    src={item.mainImageUrl}
                    alt={item.title}
                    className='h-10 object-cover rounded-md border'
                  />
                )}
              </Link>
            </li>
          ))}
        </ul>
      </TileWrapper>
    )
  }

  if (!compact) {
    return (
      <TileWrapper inContainer={false}>
        <div className='flex flex-col space-y-4'>
          {visibleNewsItems.map((item) => (
            <NewsItem
              key={item.id}
              item={item}
              inList={true}
              teamName={ownerName}
              newsPath={newsPath}
              editPermission={{
                ...recentNewsEditPermission,
                requireAllTeams:
                  recentNewsEditPermission.requireAllTeams ||
                  item.newsableType === 'Guild',
              }}
            />
          ))}
        </div>
      </TileWrapper>
    )
  }

  return (
    <TileWrapper>
      <div className='space-y-4'>
        {visibleNewsItems.length === 0 ? (
          <p className='text-muted-foreground'>No news items found.</p>
        ) : null}

        <ul>
          {visibleNewsItems.map((item, index) => (
            <li
              key={item.id}
              className={
                index === 0 && !tile.config?.title ? '' : 'mt-4 border-t pt-4'
              }
            >
              <div className='flex gap-4'>
                {item.mainImageUrl ? (
                  <img
                    src={item.mainImageUrl}
                    alt={item.title}
                    className='h-20 w-28 shrink-0 rounded-md border object-cover'
                  />
                ) : null}

                <div className='min-w-0 grow'>
                  <h5 className='mb-1'>
                    <Link
                      to={`${newsPath}/${item.id}`}
                      className='transition-colors'
                    >
                      {item.title}
                    </Link>
                  </h5>

                  <div className='flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground'>
                    {item.firstPublishedAt ? (
                      <time dateTime={item.firstPublishedAt}>
                        {dayjs(item.firstPublishedAt).format('MMMM D, YYYY')}
                      </time>
                    ) : null}
                    {item.createdBy ? (
                      <>
                        {item.firstPublishedAt ? <span>•</span> : null}
                        <span>{item.createdBy.obfuscatedBattletag}</span>
                      </>
                    ) : null}
                    {item.newsableType === 'Guild' ? null : (
                      <>
                        {item.firstPublishedAt || item.createdBy ? (
                          <span>•</span>
                        ) : null}
                        <span>{ownerName}</span>
                      </>
                    )}
                  </div>
                </div>

                <LinkButton
                  to={`${newsPath}/${item.id}`}
                  size='sm'
                  variant='outline'
                >
                  Read more
                </LinkButton>
              </div>
            </li>
          ))}
        </ul>

        {showViewAll && hasMoreNewsItems ? (
          <div className='border-t pt-4 flex justify-end'>
            <LinkButton to={newsPath} size='sm' variant='outline'>
              View all news
            </LinkButton>
          </div>
        ) : null}
      </div>
    </TileWrapper>
  )
}

export default RecentNewsTile
